/**
 * The advertised presets.
 *
 * # TRUTHFULNESS
 *
 * **Every preset here is our own first-principles approximation. None of them
 * is DStretch's.** DStretch ships as a closed-source ImageJ plugin; the plan's
 * hypothesis about how it works could not be checked without network access.
 * Milestone 8 decompiles the jar and differential-tests these matrices against
 * the real ones. Until that test passes, `provenance` stays `approximated` and
 * the UI must display the word "approximated".
 *
 * Two families:
 *
 *  - **Adaptive** (`fixed: false`). The matrix depends on the image's own
 *    covariance, which is the textbook Gillespie decorrelation stretch. Correct,
 *    but the matrix changes when the scene changes, so on live video the image
 *    flickers as the statistics move. Never the default for the camera page.
 *  - **Fixed** (`fixed: true`). A constant matrix, applied regardless of the
 *    image. Deterministic and stable on video — this is hypothesis T1, and it
 *    is what the letter names below actually are. Only the offset follows the
 *    image, and only to the image's own mean, which is what keeps a bright or
 *    dark scene off the clamp. These are the `needs: 'mean'` presets.
 *
 * The letter names (YDS, LDS, YBK, CRGB, LRD, LAB) are borrowed from DStretch's
 * vocabulary so users recognise them. The matrices behind them are ours.
 *
 * @module core/presets
 */

import type { Mat3, Preset, Stats, StretchParams, Vec3 } from './types.ts';
import { build3dLut, sample3dLut, type Lut3d } from './luts.ts';
import { rgbToLab, labToRgb } from './space.ts';

/** Build a column-major matrix from row-major literals, for readability. */
function rows(v: readonly number[]): Mat3 {
  const m = new Float64Array(9);
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) m[c * 3 + r] = v[r * 3 + c]!;
  return m;
}

/** The identity matrix. */
const I: Mat3 = Float64Array.of(1, 0, 0, 0, 1, 0, 0, 0, 1);

/**
 * The standard deviation every decorrelated band is stretched to.
 *
 * `D_ii = gain / sqrt(lambda_i)`, so band `i` leaves the stretch with standard
 * deviation exactly `gain`. The output is recentred on the image's own mean
 * (`recolorMode: 'mean'`), so an interval of `2 * gain` about that mean spans
 * the full 0..255 range. `255 / (2 * 4) = 32`, so a gain of 32 puts ±4 sigma
 * onto 0..255: the same normalisation a percentile stretch would give, arrived
 * at analytically and so usable without sorting. Only genuine outliers clip.
 *
 * Measured on the synthetic panel in `web/js/synth.ts`, gain 32 clips 1.9% of
 * channel values, against 0.05% at 24 and 3.0% at 40. What remains at 32 is the
 * panel's deliberately extreme pigment patch, a true outlier; a lower gain
 * would flatten the rock texture in order to avoid it.
 *
 * A gain of 1 (the type default) does **not** mean "leave it alone": it means
 * "unit variance", which flattens a 0..255 image almost to its own mean. Every
 * preset must therefore pass a gain.
 */
export const DEFAULT_GAIN = 32;

/**
 * The output level used by a preset with `needs: 'none'`.
 *
 * A preset that measures nothing cannot follow the image's own mean, so it must
 * anchor its output to a constant. That is the whole trade: `none` is the
 * cheapest option and the only one whose output does not move at all when the
 * scene does, but on an image whose mean is far from this constant it clips.
 *
 * No preset here uses it. It exists because `PresetNeeds` names `none` as a
 * supported mode, and this is what that mode has to anchor to.
 */
export const DEFAULT_LEVEL: Vec3 = Float64Array.of(128, 118, 104);

/**
 * Adaptive decorrelation: the textbook Gillespie stretch with no recolour.
 * Kept as the reference case, and as the "Auto" preset.
 */
const autoPreset: Preset = {
  id: 'auto',
  name: 'Auto',
  provenance: 'approximated',
  blurb: 'Adaptive decorrelation using this image’s own covariance. Recomputes per frame.',
  needs: 'covariance',
  build: (stats: Stats): StretchParams => ({
    mean: stats.mean,
    covariance: stats.covariance,
    gain: DEFAULT_GAIN,
  }),
};

/**
 * A fixed "apply the same matrix to everything" preset (plan hypothesis T1).
 *
 * The transform is
 *
 *     out = gain * R * (x - mu) + mu + o
 *
 * which is an affine with `M = gain * R` and `b = mu + o - M mu`. It amplifies
 * every deviation from the image's own mean by the same fixed amount, so the
 * *stretch* is image-independent and does not flicker on live video. Only the
 * output level `b` follows the image, and it follows it to the image's own
 * mean, which is what stops a bright or dark scene clipping.
 *
 * `R` is applied after every band already has standard deviation `gain`, so
 * `R` decides which colour difference is emphasised, not how hard.
 */
function fixedPreset(
  id: string,
  name: string,
  blurb: string,
  matrix: Mat3,
  opts: { gain?: number; bandOffset?: Vec3; outputOffset?: Vec3 } = {},
): Preset {
  const gain = opts.gain ?? FIXED_GAIN;
  return {
    id,
    name,
    provenance: 'approximated',
    fixed: true,
    needs: 'mean',
    blurb,
    build: (stats: Stats): StretchParams => ({
      // Only `b` reads this; `M = gain * R` is the same for every image.
      mean: stats.mean,
      // Unit covariance, so every `D_ii` is exactly `gain` and `M = gain * R`.
      covariance: I,
      recolor: matrix,
      recolorMode: 'mean',
      gain,
      bandOffset: opts.bandOffset,
      outputOffset: opts.outputOffset,
    }),
  };
}

/**
 * How hard the letter presets push.
 *
 * Lower than `DEFAULT_GAIN` on purpose. These matrices are near-identity, so
 * the band gain is most of the contrast the preset gets; `DEFAULT_GAIN` on top
 * would clip the rock. Milestone 5 measures the clipping and the report flags
 * anything above 2% of pixels.
 *
 * This is only the default. Each letter preset overrides it, and the chosen
 * values were measured, not guessed — see the note on each one.
 */
export const FIXED_GAIN = 4.5;

/*
 * The letter presets' recolour matrices.
 *
 * Each one routes a *colour difference* onto one output channel, because a
 * channel that averages the three inputs cannot show a difference: averaging is
 * exactly what hides pigment. The middle row is therefore a difference (its
 * entries sum to 0), and the other two rows are near-identity mixes (sum 1) so
 * the rock keeps a natural level and hue.
 *
 * These are hand-chosen starting points, not measurements. Milestone 5 scores
 * them.
 */

/**
 * YDS — "yellow stretch". Puts yellowness, `(R+G)/2 - B`, on the green output.
 * Yellow and orange pigment is what this targets.
 */
const YDS = rows([
  0.8, 0.2, 0.0,
  0.5, 0.5, -1.0,
  0.0, 0.2, 0.8,
]);

/** LDS — "light stretch". A gentler YDS for faded pigment on pale rock. */
const LDS = rows([
  0.85, 0.15, 0.0,
  0.25, 0.25, -0.5,
  0.0, 0.15, 0.85,
]);

/**
 * YBK — "yellow on black". Same yellowness axis, harder, and with the red
 * channel weighted toward green so that soot-darkened rock does not read as
 * pigment.
 */
const YBK = rows([
  0.2, 0.8, 0.0,
  0.6, 0.6, -1.2,
  0.1, 0.1, 0.8,
]);

/**
 * CRGB — "colour RGB". Not a decorrelation: a plain chroma boost about the
 * Rec.709 luma axis, which is the affine map
 *
 *     out = w' + k (x - w')      with w' = luma(x), k = 2
 *
 * written as a matrix. Each row sums to 1, so a neutral grey stays neutral and
 * luma is preserved exactly. Values are slower than the others because a
 * doubling of chroma needs no further gain.
 */
const CRGB = rows([
  1.7874, -0.7152, -0.0722,
  -0.2126, 1.2848, -0.0722,
  -0.2126, -0.7152, 1.9278,
]);

/** LRD — "light red". Puts the red-green difference `R - G` on the green output. */
const LRD = rows([
  0.7, 0.3, 0.0,
  1.0, -1.0, 0.0,
  0.0, 0.2, 0.8,
]);

/**
 * A fixed decorrelation built from a reference covariance.
 *
 * Rather than a hand-picked matrix, this fixes the *covariance* that the
 * adaptive path would have measured, producing a stable decorrelation. The
 * numbers are a plausible covariance for a rock-art photograph: high luma
 * variance, correlated channels, small blue variance.
 */
const REFERENCE_COVARIANCE: Mat3 = rows([
  900, 700, 420,
  700, 780, 380,
  420, 380, 300,
]);

function referenceDecorrelate(
  stats: Stats,
  gain: number,
  recolorMode: StretchParams['recolorMode'],
): StretchParams {
  return {
    // The covariance is the constant, so `M` is the same for every image; only
    // `b` reads the mean, and reading the image's own mean is what keeps the
    // output level from clipping.
    mean: stats.mean,
    covariance: REFERENCE_COVARIANCE,
    gain,
    recolorMode,
  };
}

/** All presets, in the order the UI shows them. */
export const PRESETS: readonly Preset[] = [
  autoPreset,
  {
    id: 'ref',
    name: 'Ref',
    provenance: 'approximated',
    blurb: 'Decorrelation against a fixed reference covariance. Stable on video.',
    fixed: true,
    needs: 'mean',
    build: (stats: Stats) => referenceDecorrelate(stats, DEFAULT_GAIN, 'mean'),
  },
  // The gains below were picked by measuring, on the synthetic panel, both the
  // pigment lift (as a multiple of the source's own pigment contrast) and the
  // fraction of channel values that hit the clamp. The shape of the result is
  // the intent: CRGB is the mildest by far, LDS is deliberately the gentlest of
  // the yellowness family, YBK is the hardest, and all of them stay under the
  // 2% the milestone-5 report flags.
  fixedPreset('yds', 'YDS', 'Yellowness on the green channel. Approximated.', YDS, { gain: 4.5 }),
  fixedPreset('lds', 'LDS', 'Gentle yellowness, for faded pigment on pale rock. Approximated.', LDS, {
    gain: 3.5,
  }),
  fixedPreset('ybk', 'YBK', 'Hard yellowness, for dark or sooty panels. Approximated.', YBK, {
    gain: 6,
  }),
  // gain 1 here is not the "flatten to unit variance" mistake: this preset has
  // unit covariance, so `D` is the identity and `M` is exactly `CRGB`. The
  // matrix already carries the whole doubling of chroma.
  fixedPreset('crgb', 'CRGB', 'Chroma doubled about the luma axis. Approximated.', CRGB, {
    gain: 1,
  }),
  fixedPreset('lrd', 'LRD', 'Red-green difference, for red ochre. Approximated.', LRD, { gain: 4.5 }),
];

/** Look a preset up by id. Throws when it does not exist. */
export function presetById(id: string, presets: readonly Preset[] = PRESETS): Preset {
  const found = presets.find((p) => p.id === id);
  if (!found) throw new Error(`unknown preset: ${id}`);
  return found;
}

/** Build the parameters for a preset from an image's statistics. */
export function presetParams(id: string, stats: Stats, presets: readonly Preset[] = PRESETS): StretchParams {
  return presetById(id, presets).build(stats);
}

// ---------------------------------------------------------------------------
// Lab-class preset (nonlinear, T3b)
// ---------------------------------------------------------------------------

/** A preset whose transform is not affine, so it needs a 3D LUT. */
export interface NonlinearPreset extends Omit<Preset, 'build'> {
  /** Build the 3D LUT. Expensive; cache it. */
  buildLut: () => Lut3d;
  /** Apply to one 0..255 RGB triple. */
  apply: (lut: Lut3d, rgb: ArrayLike<number>) => Vec3;
}

let cachedLabLut: Lut3d | null = null;

/**
 * LAB — stretch in CIE Lab rather than in RGB.
 *
 * `L` is contrast-stretched about the middle, and `a` is stretched by a larger
 * factor, because pigment lives mostly in `a`. The transform is nonlinear, so
 * it can only be tabulated: the 32-cubed LUT is 98 KB (hypothesis T3b).
 *
 * Approximated, like everything else here.
 */
export const LAB_PRESET: NonlinearPreset = {
  id: 'lab',
  name: 'LAB',
  provenance: 'approximated',
  blurb: 'Contrast stretch in CIE Lab, tabulated as a 32³ LUT. Approximated.',
  fixed: true,
  // The LUT is built once and applied to every pixel; it never reads the image.
  needs: 'none',
  buildLut: (): Lut3d => {
    if (cachedLabLut) return cachedLabLut;
    const K_L = 1.4;
    const K_AB = 2.2;
    cachedLabLut = build3dLut((r, g, b) => {
      const lab = rgbToLab(Float64Array.of(r * 255, g * 255, b * 255));
      const L = 50 + (lab[0]! - 50) * K_L;
      const A = lab[1]! * K_AB;
      const B = lab[2]! * K_AB;
      const out = labToRgb(Float64Array.of(L, A, B));
      return Float64Array.of(out[0]! / 255, out[1]! / 255, out[2]! / 255);
    });
    return cachedLabLut;
  },
  apply: (lut: Lut3d, rgb: ArrayLike<number>): Vec3 => {
    const out = sample3dLut(lut, rgb[0]! / 255, rgb[1]! / 255, rgb[2]! / 255);
    return Float64Array.of(
      clamp255(out[0]! * 255),
      clamp255(out[1]! * 255),
      clamp255(out[2]! * 255),
    );
  },
};

/** Every preset, linear and nonlinear, for the UI to list. */
export function allPresetSummaries(): Array<{ id: string; name: string; blurb: string; provenance: string }> {
  return [
    ...PRESETS.map((p) => ({ id: p.id, name: p.name, blurb: p.blurb, provenance: p.provenance })),
    { id: LAB_PRESET.id, name: LAB_PRESET.name, blurb: LAB_PRESET.blurb, provenance: LAB_PRESET.provenance },
  ];
}

/** Evaluate the Lab preset's underlying function directly, for tests. */
export function labPresetDirect(rgb: ArrayLike<number>, kL = 1.4, kAb = 2.2): Vec3 {
  const lab = rgbToLab(rgb);
  const out = labToRgb(Float64Array.of(50 + (lab[0]! - 50) * kL, lab[1]! * kAb, lab[2]! * kAb));
  return Float64Array.of(clamp255(out[0]!), clamp255(out[1]!), clamp255(out[2]!));
}

function clamp255(v: number): number {
  if (!(v > 0)) return 0;
  return v > 255 ? 255 : v;
}
